import React, { useState, useEffect, useRef } from "react";
import { BsBookmark, BsBookmarkFill } from "react-icons/bs";
import { ImSpellCheck } from "react-icons/im";
import { motion } from "framer-motion";
import 'katex/dist/katex.min.css';
import Latex from "react-latex-next";
import { IoExpand } from "react-icons/io5";

const Question = ({ setCurrentQuestion, sat, handleAnswerValueChange, handleCrossOutOptions, handleSelectOption, handleBookMark, currentQuestion }) => {
    const boxItemLeftRef = useRef();
    const [showCrossOutMenu, setShowCrossOutMenu] = useState(false);
    const [questionContent, setQuestionContent] = useState(currentQuestion.content);
    const [prompt, setprompt] = useState(currentQuestion.prompt);

    useEffect(() => {
        setQuestionContent(currentQuestion.content); // Update local state when currentQuestion changes
        setprompt(currentQuestion.prompt); // Update local state when currentQuestion changes

        const script2 = document.createElement('script');
        script2.src = "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js";
        script2.integrity = "sha384-+VBxd3r6XgURycqtZ117nYw44OOcIax56Z4dCRWbxyPt0Koah1uHoK0o4+/RRE05";
        script2.crossOrigin = "anonymous";
        script2.defer = true;

        // Handle script load
        script2.onload = () => {
            if (boxItemLeftRef.current) {
                if (window.renderMathInElement) {
                    const boxItemLeft = document.getElementById("box-item-left");
                    window.renderMathInElement(boxItemLeft, {
                        delimiters: [
                            { left: '\\(', right: '\\)', display: false },
                            { left: '\\[', right: '\\]', display: true },
                        ],
                        throwOnError: false,
                    });
                }
            }
        };
        document.body.appendChild(script2);

        // Cleanup: remove the script when component unmounts
        return () => {
            document.body.removeChild(script2);
        };
    }, [currentQuestion]);

    const preprocessOptions = (optionsString) => {
        try {
            // First, replace problematic backslashes (assuming they are not needed for JSON format)
            let formattedOptions = optionsString.replace(/\\(?![/uU])/g, '');

            // Escape double quotes inside HTML attributes
            formattedOptions = formattedOptions.replace(/<img([^>]+)>/gi, (match) => {
                return match.replace(/"/g, '\\"');
            });

            // Now try to parse the JSON
            return JSON.parse(optionsString);
        } catch (e) {
            console.error("Failed to parse options:", e);
            return []; // Fallback to an empty array if parsing fails
        }
    };

    const RenderOptions = () => {
        try {
            let options = preprocessOptions(currentQuestion.options); // Process options

            // Ensure options is always an array
            if (typeof options === "string") {
                options = JSON.parse(options); // Attempt to parse if it's still a string
            }

            return options.length > 0 ? (
                options.map((option, idx) => (
                    <Option
                        sat={sat}
                        key={idx}
                        handleCrossOutOptions={handleCrossOutOptions}
                        currentQuestion={currentQuestion}
                        handleSelectOption={handleSelectOption}
                        option={option}
                        showCrossOutMenu={showCrossOutMenu}
                        crossedOutOptions={currentQuestion.crossedOutOptions}
                    />
                ))
            ) : (
                <p>No options available</p> // Fallback if the options array is empty
            );
        } catch (error) {
            console.error("Error parsing options:", error);
            return <p>Error displaying options</p>; // Return error message if there is a parsing error
        }
    };

    // Parse the options if they are provided as a string
    let options = useRef(preprocessOptions(currentQuestion.options)).current;
    if (typeof options === "string") {
        try {
            options = JSON.parse(options);
        } catch (e) {
            console.error("Failed to parse options:", e);
            console.error("Problematic JSON:", options);
            options = []; // Fallback to an empty array if parsing fails
        }
    }

    return (<>

        <div className="flex z-[10] mt-[10px]">
            <div className="flex flex-col justify-start max-w-[50%]">
                <div>
                    <div
                        dangerouslySetInnerHTML={{ __html: currentQuestion.content }}
                        className="min-h-[40%] max-h-[420px] overflow-y-auto overflow-x-hidden my-list-style"
                    />
                </div>
            </div>

            <div className="mr-[20px] ml-[20px] w-[50%]">

                <div className="flex flex-row justify-between bg-gray-100">
                    <div className="flex flex-row gap-2">
                        <div className="flex flex-row items-center p-2">
                            <div>
                                <p>Question</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="my-2 min-w-[100px] max-w-[600px] max-h-[480px] overflow-y-auto">
                    <div dangerouslySetInnerHTML={{ __html: currentQuestion.prompt }} ref={boxItemLeftRef} id="box-item-left" className="ml-[20px]" />
                    <RenderOptions />
                </div>

            </div>
        </div>
    </>)
}
export default Question;

const Option = ({ sat, handleCrossOutOptions, currentQuestion, handleSelectOption, showCrossOutMenu, crossedOutOptions, option }) => {
    const optionLetter = option.charAt(0); // Extract the first character as the option letter
    const optionText = option.slice(2).trim(); // Extract the text part after the letter and period

    return (
        <div className="flex flex-row gap-1">
            <motion.div whileTap={{ scale: 0.98 }}
                className={`flex flex-row w-full border-[1px] rounded-md border-black gap-2 p-1 m-3 cursor-pointer relative`}
                onClick={() => handleSelectOption(currentQuestion.q_num, optionLetter.toLowerCase())}
            >
                <div className="flex flex-row items-center">
                    <p className="flex flex-row items-center justify-center p-1 w-6 h-6 border-[1px] border-black rounded-full">
                        {optionLetter}
                    </p>
                </div>
                <div className="flex flex-row items-center p-[2px]">
                    {sat === "math" || sat === "science" ? (
                        <Latex>{optionText}</Latex> // Use Latex component to render LaTeX content
                    ) : (
                        <p>{optionText}</p>
                    )}
                </div>
            </motion.div>
            {showCrossOutMenu && (
                <motion.div className="flex flex-row items-center" whileTap={{ scale: 0.97 }}>
                    {crossedOutOptions.includes(optionLetter.toLowerCase()) ? (
                        <p className="cursor-pointer underline" onClick={() => handleCrossOutOptions(currentQuestion.q_num, optionLetter.toLowerCase())}>
                            Undo
                        </p>
                    ) : (
                        <>
                            <p onClick={() => handleCrossOutOptions(currentQuestion.q_num, optionLetter.toLowerCase())} className="flex flex-row items-center justify-center p-1 w-6 h-6 border-[1px] border-black rounded-full cursor-pointer">
                                {optionLetter}
                            </p>
                            <div className="absolute w-6 h-[1.5px] bg-black top-3 cursor-pointer" onClick={() => handleCrossOutOptions(currentQuestion.q_num, optionLetter.toLowerCase())} />
                        </>
                    )}
                </motion.div>
            )}
        </div>
    );
};
